import { pickupReportingIds } from 'lib/analytics/pickupItem';
import {
  SHOPIFY_SHOP_ORIGINS,
  ShopifyStore,
} from 'lib/commerce/shopify/shopifyHelper';
import { getConsentGate } from 'lib/consent/gate';
import { isHealthItem } from 'lib/analytics/healthItems';

/** Hostname of the production storefront this shop already checks out against. */
export function tripleWhaleShop(
  store: string | null | undefined,
): string | null {
  if (!store || !(store in SHOPIFY_SHOP_ORIGINS)) return null;
  return new URL(SHOPIFY_SHOP_ORIGINS[store as ShopifyStore]).hostname;
}

/**
 * Headless bootstrap Neha sent, version 2.17.
 * The shop name is the storefront's own Shopify shop.
 */
export function tripleWhaleSnippet(shop: string): string {
  return `window.TriplePixelData={TripleName:${JSON.stringify(
    shop,
  )},ver:"2.17",plat:"SHOPIFY",isHeadless:true},function(W,H,A,L,E,_,B,N){function O(U,T,P,H,R){void 0===R&&(R=!1),H=new XMLHttpRequest,P?(H.open("POST",U,!0),H.setRequestHeader("Content-Type","text/plain")):H.open("GET",U,!0),H.send(JSON.stringify(P||{})),H.onreadystatechange=function(){4===H.readyState&&200===H.status?(R=H.responseText,U.includes("/first")?eval(R):P||(N[B]=R)):(299<H.status||H.status<200)&&T&&!R&&(R=!0,O(U,T-1,P))}}if(N=window,!N[H+"sn"]){N[H+"sn"]=1,L=function(){return Date.now().toString(36)+"_"+Math.random().toString(36)};try{A.setItem(H,1+(0|A.getItem(H)||0)),(E=JSON.parse(A.getItem(H+"U")||"[]")).push({u:location.href,r:document.referrer,t:Date.now(),id:L()}),A.setItem(H+"U",JSON.stringify(E))}catch(e){}var i,m,p;A.getItem(""+H+"C")||(_=A,A=N,A[H]||(E=A[H]=function(t,e,a){return void 0===a&&(a=[]),"State"==t?E.s:(W=L(),(E._q=E._q||[]).push([W,t,e].concat(a)),W)},E.s="Installed",E._q=[],E.ch=W,B="configSecurityConfModel",N[B]=1,O("https://conf.config-security.com/model",5),i=L(),m=A[atob("c2NyZWVu")],_.setItem("di_pmt_wt",i),p={id:i,action:"profile",avatar:_.getItem("auth-security_rand_salt_"),time:m[atob("d2lkdGg=")]+"."+m[atob("aGVpZ2h0")],host:A.TriplePixelData.TripleName,plat:A.TriplePixelData.plat,url:window.location.href.slice(0,500),ref:document.referrer,ver:A.TriplePixelData.ver},O("https://api.config-security.com/event",5,p),O("https://api.config-security.com/first?host="+A.TriplePixelData.TripleName+"&plat="+A.TriplePixelData.plat,5)))}}("","TriplePixel",localStorage);`;
}

export const TRIPLE_WHALE_HOSTS = [
  'https://api.config-security.com',
  'https://conf.config-security.com',
] as const;

type TriplePixelFn = (event: string, payload?: Record<string, unknown>) => void;

export type TripleWhaleCartLine = {
  item: string;
  q: number;
  v: string;
};

export type TripleWhaleCheckoutLine = {
  i: string;
  q: number;
  v: string;
};

type CartSnapshot = {
  lines?: {
    edges?: Array<{
      node?: {
        id?: string;
        quantity?: number;
        merchandise?: {
          id?: string;
          title?: string;
          product?: { id?: string; title?: string; tags?: readonly string[] };
        };
        attributes?: ReadonlyArray<{ key: string; value: string }>;
      };
    }>;
  };
  checkoutUrl?: string;
  buyerIdentity?: { email?: string; phone?: string };
} | null;

export type TripleWhaleCartRequest = {
  lineItemsToAdd?: ReadonlyArray<{
    merchandiseId?: string;
    quantity?: number;
    attributes?: ReadonlyArray<{ key: string; value: string }>;
  }>;
  lineItemsToModify?: ReadonlyArray<{
    id?: string;
    quantity?: number;
    merchandiseId?: string;
  }>;
};

type VariantTally = { quantity: number; productId: string | null };

type BeforeLine = { quantity: number; variantId: string | null };

/** Trailing Shopify id from a bare id or a gid://. A SKU does not match. */
export function shopifyNumericId(id: string | null | undefined): string | null {
  if (!id) return null;
  const match = /(?:^|\/)(\d+)$/.exec(id.trim());
  return match ? match[1] : null;
}

function positiveQuantity(quantity: number | undefined): number {
  return typeof quantity === 'number' && quantity > 0 ? quantity : 0;
}

function addAmount(
  map: Map<string, number>,
  key: string,
  amount: number,
): void {
  if (amount <= 0) return;
  map.set(key, (map.get(key) ?? 0) + amount);
}

function lineEdges(cart: CartSnapshot) {
  const edges = cart?.lines?.edges;
  return Array.isArray(edges) ? edges : null;
}

function tallyVariants(cart: CartSnapshot): Map<string, VariantTally> | null {
  const edges = lineEdges(cart);
  if (!edges) return null;
  const totals = new Map<string, VariantTally>();
  for (const edge of edges) {
    const pickup = pickupReportingIds(edge.node?.attributes);
    const variantId =
      pickup?.variantId ?? shopifyNumericId(edge.node?.merchandise?.id);
    if (!variantId) continue;
    const merchandise = edge.node?.merchandise;
    const name = merchandise?.product?.title ?? merchandise?.title;
    if (isHealthItem({ item_id: variantId, item_name: name })) continue;
    const productId =
      pickup?.productId ?? shopifyNumericId(merchandise?.product?.id);
    const quantity = positiveQuantity(edge.node?.quantity);
    const previous = totals.get(variantId);
    if (!previous) {
      totals.set(variantId, { quantity, productId });
      continue;
    }
    previous.quantity += quantity;
    if (!previous.productId && productId) previous.productId = productId;
  }
  return totals;
}

function beforeLines(cart: CartSnapshot): Map<string, BeforeLine> {
  const lines = new Map<string, BeforeLine>();
  for (const edge of lineEdges(cart) ?? []) {
    const id = edge.node?.id;
    if (!id) continue;
    lines.set(id, {
      quantity: positiveQuantity(edge.node?.quantity),
      variantId:
        pickupReportingIds(edge.node?.attributes)?.variantId ??
        shopifyNumericId(edge.node?.merchandise?.id),
    });
  }
  return lines;
}

function intendedQuantities(
  before: CartSnapshot,
  request: TripleWhaleCartRequest,
): Map<string, number> {
  const intent = new Map<string, number>();
  const known = beforeLines(before);

  for (const line of request.lineItemsToAdd ?? []) {
    const variantId =
      pickupReportingIds(line.attributes)?.variantId ??
      shopifyNumericId(line.merchandiseId);
    if (variantId)
      addAmount(intent, variantId, positiveQuantity(line.quantity));
  }

  for (const line of request.lineItemsToModify ?? []) {
    if (line.quantity === undefined || !line.id) continue;
    const current = known.get(line.id);
    if (!current?.variantId) continue;
    addAmount(intent, current.variantId, line.quantity - current.quantity);
  }

  return intent;
}

/**
 * One AddToCart line per variant whose quantity this request meant to raise
 * and the returned cart actually raised. `q` is the smaller of those two.
 */
export function tripleWhaleAddedLines(
  before: CartSnapshot,
  after: CartSnapshot,
  request: TripleWhaleCartRequest,
): TripleWhaleCartLine[] {
  const afterTotals = tallyVariants(after);
  if (!afterTotals) return [];
  const beforeTotals = tallyVariants(before) ?? new Map<string, VariantTally>();
  const intent = intendedQuantities(before, request);
  const lines: TripleWhaleCartLine[] = [];

  for (const [variantId, intended] of intent) {
    const gained =
      (afterTotals.get(variantId)?.quantity ?? 0) -
      (beforeTotals.get(variantId)?.quantity ?? 0);
    const quantity = Math.min(intended, gained);
    const productId = afterTotals.get(variantId)?.productId;
    if (quantity <= 0 || !productId) continue;
    lines.push({ item: productId, q: quantity, v: variantId });
  }

  return lines;
}

function triplePixel(): TriplePixelFn | null {
  if (typeof window === 'undefined') return null;
  if (!getConsentGate().attribution) return null;
  const pixel = (window as Window & { TriplePixel?: TriplePixelFn })
    .TriplePixel;
  return typeof pixel === 'function' ? pixel : null;
}

export function trackTripleWhaleCartChange(
  before: CartSnapshot,
  after: CartSnapshot,
  request: TripleWhaleCartRequest,
): void {
  const pixel = triplePixel();
  if (!pixel) return;
  for (const line of tripleWhaleAddedLines(before, after, request)) {
    pixel('AddToCart', line);
  }
}

export function tripleWhaleCheckoutLines(
  cart: CartSnapshot,
): TripleWhaleCheckoutLine[] {
  const totals = tallyVariants(cart);
  if (!totals) return [];
  const lines: TripleWhaleCheckoutLine[] = [];
  for (const [variantId, tally] of totals) {
    if (!tally.productId || tally.quantity <= 0) continue;
    lines.push({ i: tally.productId, q: tally.quantity, v: variantId });
  }
  return lines;
}

/** Token in a Shopify checkout path `/cart/c/<token>`. */
export function tripleWhaleCheckoutToken(
  checkoutUrl: string | null | undefined,
): string | null {
  if (!checkoutUrl) return null;
  try {
    const match = new URL(checkoutUrl).pathname.match(/\/cart\/c\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function trackTripleWhaleCheckoutStarted(cart: CartSnapshot): void {
  const pixel = triplePixel();
  if (!pixel) return;
  const lineItems = tripleWhaleCheckoutLines(cart);
  if (!lineItems.length) return;
  // Email and phone stay out: Legal has not assessed email/phone matching.
  const cotkn = tripleWhaleCheckoutToken(cart?.checkoutUrl);
  pixel('checkoutStarted', {
    lineItems,
    ...(cotkn ? { cotkn } : {}),
  });
}

export type TripleWhaleProductView = {
  pathname: string;
  name: string;
  id?: string;
  variant?: string;
  price?: string;
};

let pendingProductView: TripleWhaleProductView | null = null;
let sentProductPath: string | null = null;

/** True when this path's one product view is already sent or waiting for the pixel. */
export function tripleWhaleProductViewCovers(pathname: string): boolean {
  return (
    sentProductPath === pathname || pendingProductView?.pathname === pathname
  );
}

/** Drop a view for a buy page the shopper has left, so the next visit can send once. */
export function releaseTripleWhaleProductView(pathname: string): void {
  if (pendingProductView && pendingProductView.pathname !== pathname) {
    pendingProductView = null;
  }
  if (sentProductPath && sentProductPath !== pathname) sentProductPath = null;
}

/**
 * One product page view per visit to a buy page.
 * Triple Whale records it as `pageLoad` with product context.
 * Nothing is sent until the attribution pixel is on the page.
 */
export function trackTripleWhaleProductView(
  view: TripleWhaleProductView,
): void {
  const id = shopifyNumericId(view.id) ?? '';
  const variant = shopifyNumericId(view.variant) ?? '';
  if (!view.pathname || !view.name || (!id && !variant)) return;
  if (isHealthItem({ item_id: variant, item_name: view.name })) return;
  if (sentProductPath === view.pathname) return;
  pendingProductView = {
    pathname: view.pathname,
    name: view.name,
    id,
    variant,
    price: view.price ?? '',
  };
  flushTripleWhaleProductView();
}

export function flushTripleWhaleProductView(): void {
  const view = pendingProductView;
  if (!view || sentProductPath === view.pathname) return;
  const pixel = triplePixel();
  if (!pixel) return;
  pixel('pageLoad', {
    product: {
      id: view.id ?? '',
      name: view.name,
      price: view.price ?? '',
      variant: view.variant ?? '',
    },
  });
  sentProductPath = view.pathname;
  pendingProductView = null;
}

export function trackTripleWhalePageLoad(): void {
  triplePixel()?.('pageLoad');
}
